import { supabase } from '../lib/supabase';
import { activityService } from './activityService';

const BUCKET = 'task-photos';

// Keep in sync with the task-photos bucket's allowed_mime_types and with
// MobilePhotoInput.jsx. HEIC/HEIF included for iOS Photos-app captures;
// GIF intentionally excluded since task evidence photos are stills.
const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
const ALLOWED_EXTENSIONS_LABEL = 'JPEG, PNG, WebP, HEIC, HEIF';

// iOS Safari often hands File objects with `file.type === ''` for images
// shared from the Photos app. Supabase-js derives the Content-Type header
// from `file.type`; an empty string yields a missing or generic header that
// the bucket allowlist rejects. Fall back to a MIME inferred from the
// filename extension so the same file that works on desktop also works on
// iOS. Mirrors the fix in taskCompletionPhotosService.js.
const EXT_TO_MIME = {
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png',
  webp: 'image/webp', heic: 'image/heic', heif: 'image/heif',
};

const mapPhotoFromDB = (row) => ({
  id: row?.id,
  taskId: row?.task_id,
  siteId: row?.site_id,
  uploadedBy: row?.uploaded_by,
  filePath: row?.file_path,
  fileName: row?.file_name,
  fileSize: row?.file_size,
  mimeType: row?.mime_type,
  caption: row?.caption,
  clientVisible: row?.client_visible !== false,
  createdAt: row?.created_at,
  task: row?.task || null,
  site: row?.site || null,
  uploader: row?.uploader || null,
});

/**
 * Error-surfacing pattern: see the canonical doc-comment at the top of
 * src/services/clientService.js. Services preserve the real error object on
 * `{ data, error }` (no `{ message }` stripping); page-layer callers branch
 * on `error?.code === 'PGRST301'` for RLS copy and otherwise surface the
 * real `error.message` alongside any friendly intro text.
 *
 * Phase 3 note: this service previously returned `error` as a plain string,
 * which is even less informative than the `{ message }` shell — losing not
 * just `code/details/hint` but also any non-message metadata. The migration
 * keeps the `{ data, error }` and `{ error }` slot shapes that callers
 * already destructure, but `error` is now a real Error/PostgrestError.
 */
function surfaceError(scope, error) {
  console.error(`[taskPhotoService] ${scope} failed:`, {
    code: error?.code,
    message: error?.message,
    details: error?.details,
    hint: error?.hint,
  });
  return error;
}

export const taskPhotoService = {
  async uploadPhoto(file, taskId, siteId, caption = '') {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const ext = file?.name?.split('.')?.pop()?.toLowerCase() || 'jpg';
      const timestamp = Date.now();
      const safeName = file?.name?.replace(/[^a-zA-Z0-9._-]/g, '_');
      // Site-direct uploads (e.g. the worker dashboard "Add Photo" action) carry
      // no task, so task_id is null. Foldering by task would yield ".../null/...";
      // use a dedicated site-direct namespace keyed by site instead.
      const pathSegment = taskId ? taskId : `site-direct/${siteId}`;
      const filePath = `${user?.id}/${pathSegment}/${timestamp}-${safeName}`;

      const contentType = file?.type || EXT_TO_MIME?.[ext] || '';

      // Reject unsupported types up front with a typed error so the user
      // never waits on a Supabase round-trip to learn their file was invalid.
      if (!ALLOWED_MIME_TYPES?.includes(contentType)) {
        const err = new Error(`Unsupported file type. Allowed: ${ALLOWED_EXTENSIONS_LABEL}.`);
        err.code = 'UNSUPPORTED_MIME';
        return { data: null, error: err };
      }

      const { error: uploadError } = await supabase?.storage
        ?.from(BUCKET)
        ?.upload(filePath, file, { cacheControl: '3600', upsert: false, contentType });

      if (uploadError) {
        // Keep the existing rich log line — the original error already carries
        // these fields, but storage errors don't always have a `code`, so this
        // captures filename/size/MIME context the surface log wouldn't show.
        console.error('[taskPhotoService] storage upload failed:', uploadError?.message, { filePath, contentType, fileType: file?.type, fileSize: file?.size });
        // Supabase Storage returns 413 (Payload Too Large) as `statusCode`
        // (string) on the error object. Translate to an actionable message
        // so the user knows the cause is file size, not a generic upload
        // failure. The 25MB ceiling matches the task-photos bucket policy.
        const statusCode = String(uploadError?.statusCode ?? '');
        const msgStr = String(uploadError?.message ?? '');
        if (statusCode === '413' || /exceeded.*size|too large|payload too large/i.test(msgStr)) {
          const friendly = new Error('File is too large. Maximum size is 25MB.');
          friendly.code = 'PAYLOAD_TOO_LARGE';
          friendly.statusCode = 413;
          friendly.cause = uploadError;
          throw friendly;
        }
        throw uploadError;
      }

      const { data, error: insertError } = await supabase
        ?.from('task_photos')
        ?.insert({
          task_id: taskId,
          site_id: siteId,
          uploaded_by: user?.id,
          file_path: filePath,
          file_name: file?.name,
          file_size: file?.size,
          mime_type: contentType,
          caption: caption || null,
        })
        ?.select(`
          *,
          task:daily_tasks!task_photos_task_id_fkey(id, title, sites(id, company_id)),
          site:sites!task_photos_site_id_fkey(id, company_id)
        `)
        ?.single();

      if (insertError) throw insertError;

      if (siteId) {
        const companyId = data?.site?.company_id || data?.task?.sites?.company_id || null;

        if (taskId) {
          const taskTitle = data?.task?.title || 'Unknown task';
          const { count } = await supabase
            ?.from('task_photos')
            ?.select('id', { count: 'exact', head: true })
            ?.eq('task_id', taskId);
          await activityService?.createActivity({
            companyId,
            siteId,
            activityType: 'photo_evidence_added',
            title: `Photo evidence added: ${taskTitle}`,
            description: `Photo uploaded for task "${taskTitle}" (${count || 1} photo${(count || 1) !== 1 ? 's' : ''} total)`,
            relatedEntityId: data?.id,
            metadata: { taskId, photoId: data?.id, fileName: file?.name }
          });
        } else {
          // Site-direct upload (no task) — e.g. worker dashboard "Add Photo".
          await activityService?.createActivity({
            companyId,
            siteId,
            activityType: 'photo_evidence_added',
            title: 'Site photo added',
            description: 'A site photo was uploaded to the gallery.',
            relatedEntityId: data?.id,
            metadata: { photoId: data?.id, fileName: file?.name }
          });
        }
      }

      return { data: mapPhotoFromDB(data), error: null };
    } catch (error) {
      return { data: null, error: surfaceError('uploadPhoto', error) };
    }
  },

  async getSignedUrl(filePath, expiresIn = 3600) {
    try {
      const { data, error } = await supabase?.storage
        ?.from(BUCKET)
        ?.createSignedUrl(filePath, expiresIn);
      if (error) throw error;
      return { url: data?.signedUrl, error: null };
    } catch (error) {
      return { url: null, error: surfaceError('getSignedUrl', error) };
    }
  },

  async getPhotosByTask(taskId) {
    try {
      const { data, error } = await supabase
        ?.from('task_photos')
        ?.select(`
          *,
          uploader:user_profiles!task_photos_uploaded_by_fkey(id, full_name)
        `)
        ?.eq('task_id', taskId)
        ?.order('created_at', { ascending: false });
      if (error) throw error;
      return { data: (data || [])?.map(mapPhotoFromDB), error: null };
    } catch (error) {
      return { data: [], error: surfaceError('getPhotosByTask', error) };
    }
  },

  async getPhotosBySite(siteId, filters = {}) {
    try {
      let query = supabase
        ?.from('task_photos')
        ?.select(`
          *,
          task:daily_tasks!task_photos_task_id_fkey(id, title, category, task_date),
          site:sites!task_photos_site_id_fkey(id, site_name),
          uploader:user_profiles!task_photos_uploaded_by_fkey(id, full_name)
        `)
        ?.eq('site_id', siteId);

      if (filters?.uploadedBy) query = query?.eq('uploaded_by', filters?.uploadedBy);
      if (filters?.dateFrom) query = query?.gte('created_at', filters?.dateFrom);
      if (filters?.dateTo) query = query?.lte('created_at', filters?.dateTo + 'T23:59:59');

      const { data, error } = await query?.order('created_at', { ascending: false });
      if (error) throw error;
      return { data: (data || [])?.map(mapPhotoFromDB), error: null };
    } catch (error) {
      return { data: [], error: surfaceError('getPhotosBySite', error) };
    }
  },

  // Lightweight site preview used by /site-manager-dashboard. Same join shape
  // as getPhotosBySite so the dashboard panel can reuse PhotoDetailModal, but
  // with a small `limit` so the dashboard query stays cheap on sites with
  // hundreds of photos.
  async getRecentBySite(siteId, limit = 6) {
    try {
      const { data, error } = await supabase
        ?.from('task_photos')
        ?.select(`
          *,
          task:daily_tasks!task_photos_task_id_fkey(id, title, category, task_date),
          site:sites!task_photos_site_id_fkey(id, site_name),
          uploader:user_profiles!task_photos_uploaded_by_fkey(id, full_name)
        `)
        ?.eq('site_id', siteId)
        ?.order('created_at', { ascending: false })
        ?.limit(limit);
      if (error) throw error;
      return { data: (data || [])?.map(mapPhotoFromDB), error: null };
    } catch (error) {
      return { data: [], error: surfaceError('getRecentBySite', error) };
    }
  },

  async getAllPhotos(filters = {}) {
    try {
      let query = supabase
        ?.from('task_photos')
        ?.select(`
          *,
          task:daily_tasks!task_photos_task_id_fkey(id, title, category, task_date),
          site:sites!task_photos_site_id_fkey(id, site_name),
          uploader:user_profiles!task_photos_uploaded_by_fkey(id, full_name)
        `);

      if (filters?.siteId && filters?.siteId !== 'all') query = query?.eq('site_id', filters?.siteId);
      if (filters?.uploadedBy && filters?.uploadedBy !== 'all') query = query?.eq('uploaded_by', filters?.uploadedBy);
      if (filters?.dateFrom) query = query?.gte('created_at', filters?.dateFrom);
      if (filters?.dateTo) query = query?.lte('created_at', filters?.dateTo + 'T23:59:59');

      const { data, error } = await query?.order('created_at', { ascending: false });
      if (error) throw error;
      return { data: (data || [])?.map(mapPhotoFromDB), error: null };
    } catch (error) {
      return { data: [], error: surfaceError('getAllPhotos', error) };
    }
  },

  async setClientVisible(photoId, clientVisible) {
    try {
      // `.select().maybeSingle()` forces PostgREST to return the updated row
      // so we can detect silent no-op writes (e.g. RLS filtered the row out
      // of the UPDATE set, or the id didn't match). Without this, Supabase
      // returns { error: null } even when zero rows were affected, and the
      // UI shows a "successful" toggle that never persisted.
      const { data, error } = await supabase
        ?.from('task_photos')
        ?.update({ client_visible: !!clientVisible })
        ?.eq('id', photoId)
        ?.select('id, client_visible')
        ?.maybeSingle();
      if (error) throw error;
      if (!data) {
        const err = new Error('Visibility update did not affect any row — permission denied or photo not found.');
        err.code = 'NO_ROW_AFFECTED';
        throw err;
      }
      return { data, error: null };
    } catch (error) {
      return { error: surfaceError('setClientVisible', error) };
    }
  },

  async deletePhoto(photoId, filePath) {
    try {
      const { error: storageError } = await supabase?.storage
        ?.from(BUCKET)
        ?.remove([filePath]);
      if (storageError) console.warn('[taskPhotoService] storage delete error (non-fatal):', storageError?.message);

      const { error } = await supabase?.from('task_photos')?.delete()?.eq('id', photoId);
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: surfaceError('deletePhoto', error) };
    }
  },
};
